import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

/** Measure content height (ResizeObserver + fallbacks) */
function useMeasuredHeight(open) {
  const innerRef = useRef(null);
  const [h, setH] = useState(0);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => setH(el.scrollHeight);
    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // re-measure right after toggle so layout is committed
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => setH(el.scrollHeight));
    return () => cancelAnimationFrame(id);
  }, [open]);

  return { innerRef, h };
}

/** Single animated FAQ row
 * - Uncontrolled if `open` prop is undefined (uses internal state)
 * - Controlled if `open` prop is boolean (parent drives state via onToggle)
 */
export function FAQItem({
  q,
  a,
  defaultOpen = false,
  open: openProp,              // optional controlled prop
  onToggle,                    // optional callback(nextOpen:boolean)
}) {
  const isControlled = typeof openProp === "boolean";
  const [openUncontrolled, setOpenUncontrolled] = useState(defaultOpen);
  const open = isControlled ? openProp : openUncontrolled;

  // if parent switches `openProp`, sync internal state for consistency
  useEffect(() => {
    if (isControlled) {
      setOpenUncontrolled(openProp);
    }
  }, [isControlled, openProp]);

  const id = useId();
  const labelId = `${id}-label`;
  const contentId = `${id}-content`;

  const { innerRef, h } = useMeasuredHeight(open);
  const heightAnim = open ? h : 0;

  const handleClick = () => {
    const next = !open;
    if (isControlled) {
      onToggle?.(next);
    } else {
      setOpenUncontrolled(next);
      onToggle?.(next);
    }
  };

  return (
    <div className="accordion mb-2">
      <button
        id={labelId}
        type="button"
        className="w-full text-left px-5 py-4 font-bold flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4FF00]/60 rounded-xl"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={handleClick}
      >
        {q}
        <span
          className="ml-3 text-[#D4FF00] font-black select-none transition-transform duration-200"
          aria-hidden
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          +
        </span>
      </button>

      <AnimatePresence initial={false}>
        {(open || h > 0) && (
          <motion.div
            key="content"
            id={contentId}
            role="region"
            aria-labelledby={labelId}
            className="overflow-hidden will-change-[height,opacity]"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: heightAnim, opacity: open ? 1 : 0 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.18, ease: "linear" },
            }}
          >
            <div
              ref={innerRef}
              className="content px-5 pb-4"
              style={{ margin: 0, paddingTop: 0 }}
            >
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** List wrapper: pass items as [question, answer] tuples */
export default function FaqList({
  items = [],
  defaultOpenIndex = null,
  singleOpen = false,
}) {
  const [openIndex, setOpenIndex] = useState(
    singleOpen ? defaultOpenIndex : null
  );

  return (
    <div>
      {items.map(([q, a], i) => {
        if (singleOpen) {
          const isOpen = i === openIndex;
          return (
            <FAQItem
              key={q}
              q={q}
              a={a}
              open={isOpen}                 // controlled
              onToggle={(nextOpen) =>
                setOpenIndex(nextOpen ? i : null)
              }
            />
          );
        }
        // uncontrolled items (each manages self), keep defaultOpen for the initial one if provided
        return (
          <FAQItem
            key={q}
            q={q}
            a={a}
            defaultOpen={i === defaultOpenIndex}
          />
        );
      })}
    </div>
  );
}
